import React, { useCallback, useState } from "react";
import { DefaultButton, Spinner, SpinnerSize } from "@fluentui/react";
import { useHistory, useParams } from "react-router-dom";
import ManagementAuthorizationConfirm from "@/components/ManagementAuthorization/ManagementAuthorizationConfirm";
import savingsPlans from "@/services/SavingsPlanService";
import { mapAxiosErrorToOnboardingEnvelope } from "@/services/SavingsPlanOnboarding.types";
import { ContractOnboardingStrings, resolveContractOnboardingError } from "@/screen/Map/contractOnboarding.strings";
import { buildMapNavigationState } from "@/screen/Map/resolveContractOnboardingStepNavigation";
import { useContractStepGuard } from "@/screen/ContractManagement/hooks/useContractStepGuard";

/**
 * Paso 2.2.3 — firma OTP de autorización de gestión.
 * Al completar todas las firmas: advancePlanStep → mapa (2.3).
 */
const ContractManagementAuthorizationConfirmScreen: React.FC = () =>
{
    const { savingsPlanId: savingsPlanIdParam } = useParams<{savingsPlanId: string}>();
    const savingsPlanId = Number(savingsPlanIdParam);
    const history = useHistory();
    const guard = useContractStepGuard(savingsPlanId, ["2.2.3", "2.4.4"]);
    const [advanceError, setAdvanceError] = useState<string | null>(null);
    const [isRetrying, setIsRetrying] = useState<boolean>(false);

    // Intenta avanzar el paso. Las firmas ya están persistidas en BD, así que se puede
    // reintentar cuantas veces haga falta sin volver a firmar. Devuelve true si avanza.
    // El nombre del plan solo se usa para el state de navegación al mapa: se pide DESPUÉS
    // del avance y con try/catch propio. Si falla getSavingsPlan, igual navegamos (sin nombre);
    // si falla la red/403/500 en el avance, setAdvanceError + reintento (nunca silent catch).
    const advance = useCallback(async (): Promise<boolean> =>
    {
        setAdvanceError(null);

        try
        {
            // Guarda de sincronía: se declara el paso que la guarda resolvió como vivo (2.2.3 en el
            // tronco, 2.4.4 en la rama de mayoría de edad).
            if(guard.activeStepKey === null)
            {
                return false;
            }

            const result = await savingsPlans.advancePlanStep(savingsPlanId, guard.activeStepKey);

            if(!result.success)
            {
                setAdvanceError(resolveContractOnboardingError(result.errors?.[0], ContractOnboardingStrings.continueContractFallbackError));
                return false;
            }

            let savingsPlanName: string | undefined;
            try
            {
                const plan = await savingsPlans.getSavingsPlan(savingsPlanId);
                savingsPlanName = plan?.name;
            }
            catch
            {
                // El avance ya se completó; el nombre es cosmético para el título del mapa.
            }

            history.push("/configurationMap", buildMapNavigationState(savingsPlanId, savingsPlanName));
            return true;
        }
        catch(error: unknown)
        {
            const envelope = mapAxiosErrorToOnboardingEnvelope(error);
            setAdvanceError(resolveContractOnboardingError(envelope.errors?.[0], ContractOnboardingStrings.continueContractFallbackError));
            return false;
        }
    }, [guard.activeStepKey, history, savingsPlanId]);

    const onAllSigned = useCallback(async () =>
    {
        const advanced = await advance();
        if(!advanced)
        {
            // Señalamos el fallo al componente de firma para que NO abra el modal de éxito.
            throw new Error("ADVANCE_STEP_FAILED");
        }
    }, [advance]);

    const onRetry = useCallback(async () =>
    {
        setIsRetrying(true);
        try
        {
            await advance();
        }
        finally
        {
            setIsRetrying(false);
        }
    }, [advance]);

    if(guard.isChecking)
    {
        return(<Spinner size={SpinnerSize.large} style={{marginTop: "2rem"}} />);
    }

    return(
        <React.Fragment>
            <ManagementAuthorizationConfirm cancelPath={`/contract/${savingsPlanId}/authorization`} onAllSigned={onAllSigned} />
            {advanceError &&
            (
                <div style={{padding: "16px 24px", color: "#a4262c", fontSize: "15px"}}>
                    <div>{advanceError}</div>
                    <DefaultButton className="button tiny-primary-button"
                        style={{marginTop: "12px"}}
                        disabled={isRetrying}
                        onClick={() =>
                        {
                            void onRetry();
                        }}
                    >
                        {isRetrying &&
                        (
                            <Spinner size={SpinnerSize.small} className="button-spinner" />
                        )}
                        {ContractOnboardingStrings.retry}
                    </DefaultButton>
                </div>
            )}
        </React.Fragment>
    );
};

export default ContractManagementAuthorizationConfirmScreen;